import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import { User } from "users/user";
import { ProposalChangeAction, ProposalSpecChange } from "work/proposals/__builder/proposal-builder";
import ProposalSchema, { ProposalParams } from "work/proposals/_schemas/proposal-schema";
import ProposalFieldSchema from "work/proposals/proposal-fields/_schema/proposal-field-schema";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import WorkAgreementSchema from "work/work-agreements/_schemas/work-agreement-codec";
import { z } from "zod";

export type ProposalBuilderJSON = z.infer<typeof ProposalBuilderSchema.ProposalBuilderJSONZ>;
export type ProposalBuilderParams = z.infer<typeof ProposalBuilderSchema.ProposalBuilderZ>;
export type ProposalSpecChangeJSON = z.infer<typeof ProposalSpecChangeSchema.ProposalSpecChangeJSONZ>;
export type ProposalSpecChangeParams = z.infer<typeof ProposalSpecChangeSchema.ProposalSpecChangeZ>;
export type ProposalChangeActionParams = z.infer<typeof ProposalChangeActionSchema>;
export type ProposalChangeActionPresentTense = z.infer<typeof ProposalChangeActionPresentTenseSchema>;
export type ProposalChangeActionDescription = z.infer<typeof ProposalChangeActionDescriptionSchema>;

export const ProposalChangeActionPresentTenseSchema = z.union([
  z.literal("Edit"),
  z.literal("ReEdit"),
  z.literal("Add"),
  z.literal("Remove"),
]);

export const ProposalChangeActionDescriptionSchema = z.union([
  z.literal("Edited"),
  z.literal("Re-edited"),
  z.literal("Added"),
  z.literal("Removed"),
]);

export const ProposalChangeActionSchema = z.object({
  presentTense: ProposalChangeActionPresentTenseSchema,
  actionDescription: ProposalChangeActionDescriptionSchema,
});

export class ProposalSpecChangeSchema {
  public static readonly ProposalSpecChangeJSONZ = z.object({
    field: ProposalFieldSchema.ProposalFieldJSONZ,
    action: ProposalChangeActionSchema,
    value: z.string(),
    previousValue: z.string().optional(),
    nextValue: z.string().optional(),
  });

  public static readonly ProposalSpecChangeZ = z.object({
    field: z.instanceof(ProposalField),
    get action() {
      return z.instanceof(ProposalChangeAction);
    },
    value: z.string(),
    previousValue: z.string().optional(),
    nextValue: z.string().optional(),
  });

  public static validateJSON(json: unknown): ProposalSpecChangeJSON {
    return parseOrThrow(ProposalSpecChangeSchema.ProposalSpecChangeJSONZ, json, {
      logError: true,
      schemaName: "ProposalSpecChangeSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is ProposalSpecChangeJSON {
    try {
      ProposalSpecChangeSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static validate(values: unknown): ProposalSpecChangeParams {
    return parseOrThrow(ProposalSpecChangeSchema.ProposalSpecChangeZ, values, {
      logError: true,
      schemaName: "ProposalSpecChangeSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is ProposalSpecChangeParams {
    try {
      ProposalSpecChangeSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): ProposalSpecChangeParams {
    const parsed = parseOrThrow(ProposalSpecChangeSchema.ProposalSpecChangeJSONZ, json, {
      logError: true,
      schemaName: "ProposalSpecChangeSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return this.codec.decode(parsed);
  }

  public static serialize(params: ProposalSpecChangeParams): ProposalSpecChangeJSON {
    const parsed = ProposalSpecChangeSchema.validate(params);
    return this.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    ProposalSpecChangeSchema.ProposalSpecChangeJSONZ,
    ProposalSpecChangeSchema.ProposalSpecChangeZ,
    {
      decode: (data: ProposalSpecChangeJSON) => {
        const fieldParams = ProposalFieldSchema.deserialize(data.field);
        const actionParams = new ProposalChangeAction(ProposalChangeActionSchema.parse(data.action));
        return {
          field: fieldParams === "general" ? ProposalField.General : new ProposalField(fieldParams),
          action: actionParams,
          value: data.value,
          previousValue: data.previousValue,
          nextValue: data.nextValue,
        };
      },
      encode: (builder) => ({
        field: builder.field.toJSON(),
        action: builder.action.toJSON(),
        value: builder.value,
        previousValue: builder.previousValue,
        nextValue: builder.nextValue,
      }),
    },
  );
}

export default class ProposalBuilderSchema {
  public static readonly ProposalBuilderJSONZ = z.object({
    get specStack() {
      return z.array(ProposalSchema.ProposalJSONWithoutBuilderZ);
    },
    currentIndex: z.number(),
    lastSavedIndex: z.number().optional(),
    sessionHistory: z.array(ProposalSpecChangeSchema.ProposalSpecChangeJSONZ),
    lastUpdated: z.iso.datetime({ offset: true }),
  });

  public static readonly ProposalBuilderZ = z.object({
    get specStack() {
      return z.array(ProposalSchema.ProposalZ);
    },
    currentIndex: z.number(),
    lastSavedIndex: z.number().optional(),
    get sessionHistory() {
      return z.array(z.instanceof(ProposalSpecChange));
    },
    lastUpdated: z.instanceof(AHDate),
  });

  public static validateJSON(json: unknown): ProposalBuilderJSON {
    return parseOrThrow(ProposalBuilderSchema.ProposalBuilderJSONZ, json, {
      logError: true,
      schemaName: "ProposalBuilderSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is ProposalBuilderJSON {
    try {
      ProposalBuilderSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static validate(values: unknown): ProposalBuilderParams {
    return parseOrThrow(ProposalBuilderSchema.ProposalBuilderZ, values, {
      logError: true,
      schemaName: "ProposalBuilderSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is ProposalBuilderParams {
    try {
      ProposalBuilderSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public constructor(
    private readonly workAgreementSchema: WorkAgreementSchema,
    private readonly currentUser: User,
  ) {}

  public deserialize(json: unknown): ProposalBuilderParams {
    const parsed = parseOrThrow(ProposalBuilderSchema.ProposalBuilderJSONZ, json, {
      logError: true,
      schemaName: "ProposalBuilderSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return this.codec.decode(parsed);
  }

  public serialize(params: ProposalBuilderParams): ProposalBuilderJSON {
    const parsed = ProposalBuilderSchema.validate(params);
    return this.codec.encode(parsed) as ProposalBuilderJSON;
  }

  private readonly codec = z.codec(ProposalBuilderSchema.ProposalBuilderJSONZ, ProposalBuilderSchema.ProposalBuilderZ, {
    decode: (data: ProposalBuilderJSON) => ({
      currentIndex: data.currentIndex,
      lastSavedIndex: data.lastSavedIndex,
      specStack: data.specStack.map((proposalJson) =>
        new ProposalSchema(this.workAgreementSchema, this.currentUser).deserialize(proposalJson),
      ),
      sessionHistory: data.sessionHistory.map(
        (entry) => new ProposalSpecChange(ProposalSpecChangeSchema.deserialize(entry)),
      ),
      lastUpdated: new AHDate(data.lastUpdated),
    }),
    encode: (builder) => ({
      currentIndex: builder.currentIndex,
      lastSavedIndex: builder.lastSavedIndex,
      specStack: builder.specStack.map((proposalSpec) =>
        new ProposalSchema(this.workAgreementSchema, this.currentUser).serialize(proposalSpec as ProposalParams),
      ),
      lastUpdated: builder.lastUpdated.toISOString(),
      sessionHistory: builder.sessionHistory.map((entry) => entry.toJSON()),
    }),
  });
}
